import { Text } from '@/components/text';
import { Button } from '@/components/ui/button';
import { Colors } from '@/constants/theme';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { signInWithEmailAndPassword } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';

export default function AdminScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { setUser, isLoggedIn } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      router.replace('/');
    }
  }, [isLoggedIn, router]);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      setUser(userCredential.user);
      router.replace('/');
    } catch (err: any) {
      console.error(err);
      setError(t('auth.error', 'Login failed. Please check your credentials.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.container}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Image
            source={require('@/assets/images/logo.png')}
            style={{ width: 200, height: 60 }}
            contentFit='contain'
            alt={t('common.logoAlt')}
          />
          <View style={styles.headerTextContainer}>
            <Text type='title' style={styles.title}>
              {t('auth.adminLogin', 'Login to your account')}
            </Text>
            <Text style={styles.description}>
              {t('auth.adminLoginDescription', 'Enter your email below to login to your account')}
            </Text>
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>{t('auth.email', 'Email')}</Text>
          <Controller
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                style={styles.input}
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                placeholder='admin@example.com'
                placeholderTextColor={Colors.light.placeholderText}
                autoCapitalize='none'
                keyboardType='email-address'
              />
            )}
            name='email'
          />
          <View style={styles.inputErrorContainer}>
            {errors.email && <Text style={styles.errorText}>{t('auth.emailRequired', 'Email is required.')}</Text>}
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>{t('auth.password', 'Password')}</Text>
          <Controller
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.passwordContainer}>
                <TextInput
                  style={styles.passwordInput}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  placeholder='••••••••'
                  placeholderTextColor={Colors.light.placeholderText}
                  secureTextEntry={!showPassword}
                />
                <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
                  <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color='#666' />
                </TouchableOpacity>
              </View>
            )}
            name='password'
          />
          <View style={styles.inputErrorContainer}>
            {errors.password && (
              <Text style={styles.errorText}>{t('auth.passwordRequired', 'Password is required.')}</Text>
            )}
          </View>
        </View>
        <TouchableOpacity onPress={() => router.push('/forgot-password')} style={{ alignSelf: 'flex-end' }}>
          <Text style={styles.forgotPassword}>{t('auth.forgotPassword', 'Forgot Password?')}</Text>
        </TouchableOpacity>

        <View style={styles.messageContainer}>{error && <Text style={styles.errorText}>{error}</Text>}</View>

        <Button
          style={{ width: '100%', alignItems: 'center' }}
          onPress={handleSubmit(onSubmit)}
          label={loading ? t('auth.loggingIn', 'Logging in...') : t('auth.login', 'Login')}
          disabled={loading}
        />

        <View style={{ marginTop: 16, alignItems: 'center' }}>
          <Button variant='ghost' label={t('common.back', 'Back to Home')} onPress={() => router.navigate('/')} />
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 32,
    width: '100%',
    maxWidth: 450,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    gap: 5,
  },
  header: {
    alignItems: 'center',
    marginBottom: 20,
  },
  headerTextContainer: {
    width: '100%',
    marginTop: 20,
  },
  title: {
    fontSize: 16,
  },
  description: {
    fontSize: 14,
    color: Colors.light.secondaryText,
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '500',
    color: '#333',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  errorText: {
    color: '#FF3B30',
    fontSize: 14,
  },
  successText: {
    color: '#34C759',
    fontSize: 14,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
  },
  passwordInput: {
    flex: 1,
    padding: 12,
    fontSize: 16,
  },
  eyeIcon: {
    padding: 12,
  },
  forgotPassword: {
    fontWeight: '500',
    fontSize: 14,
  },
  messageContainer: {
    height: 24, // Fixed height to prevent layout shift
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 4,
  },
  inputErrorContainer: {
    height: 20, // Reserve space for one line of error text
    justifyContent: 'center',
  },
});
